import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Star,
  Search,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  ShieldCheck,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { adminApi } from '../services/api.js';
import type { AdminStarsOrderItem } from '@nova-mine/shared';

export const PaymentsView: React.FC = () => {
  const [orders, setOrders] = useState<AdminStarsOrderItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await adminApi.getStarsOrders(100, statusFilter, searchQuery);
      setOrders(res.orders);
    } catch (err: unknown) {
      console.error('[Load Stars Orders Error]', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch Stars orders');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadOrders();
  };

  const paidCount = orders.filter((o) => o.status === 'paid').length;
  const pendingCount = orders.filter((o) => o.status === 'pending').length;
  const cancelledCount = orders.filter((o) => o.status === 'cancelled' || o.status === 'failed').length;

  return (
    <div className="space-y-4">
      {/* Anti-Duplicate Protection Banner */}
      <div className="glass-panel p-4 rounded-2xl border border-emerald-500/20 bg-emerald-950/20 flex items-start space-x-3 text-xs text-emerald-200">
        <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-white">Idempotent Webhook Engine: </span>
          <span>
            Every Telegram charge ID is checked against a database UNIQUE constraint. If Telegram redelivers a webhook notification, it is safely verified without double-crediting balances or activating duplicate Stars Miners.
          </span>
        </div>
      </div>

      <div className="glass-panel rounded-3xl border border-white/10 overflow-hidden">
        {/* Toolbar */}
        <div className="p-5 border-b border-white/5 flex flex-wrap items-center justify-between gap-4 bg-space-950/40">
          <div>
            <h3 className="text-base font-black text-white flex items-center space-x-2">
              <Star className="w-5 h-5 text-nova-gold fill-nova-gold" />
              <span>Telegram Stars Orders & Payment Ledger</span>
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Authoritative order tracking with unique Telegram charge references and payload verification.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex bg-space-900 border border-white/10 rounded-xl p-1 text-xs font-bold">
              {[
                { id: 'all', label: 'All Orders' },
                { id: 'paid', label: 'Successful (Paid)' },
                { id: 'pending', label: 'Pending' },
                { id: 'cancelled', label: 'Cancelled' },
                { id: 'failed', label: 'Failed' },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStatusFilter(s.id)}
                  className={`px-3 py-1.5 rounded-lg transition-all ${
                    statusFilter === s.id
                      ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-black shadow-md'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search charge, order, user..."
                className="bg-space-900 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-nova-gold w-56"
              />
            </form>

            <button
              onClick={loadOrders}
              disabled={isLoading}
              className="p-2 rounded-xl bg-space-850 hover:bg-space-800 text-gray-300 border border-white/10"
              title="Refresh"
            >
              <RotateCcw className={`w-4 h-4 ${isLoading ? 'animate-spin text-nova-gold' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="m-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-space-900/90 border-b border-white/5 text-gray-400 font-bold uppercase tracking-wider text-[11px]">
              <tr>
                <th className="px-5 py-3.5">Order ID</th>
                <th className="px-5 py-3.5">User</th>
                <th className="px-5 py-3.5">Stars Amount</th>
                <th className="px-5 py-3.5">Units</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5">Telegram Charge ID</th>
                <th className="px-5 py-3.5">Invoice Payload</th>
                <th className="px-5 py-3.5">Created At</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-gray-300">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    <Loader2 className="w-6 h-6 animate-spin text-nova-gold mx-auto mb-2" />
                    <span>Loading Stars orders...</span>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No matching Stars orders found.
                  </td>
                </tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-white/[0.02] transition-colors">
                    {/* Order ID */}
                    <td className="px-5 py-4 font-mono text-gray-300" title={o.id}>
                      {o.id.slice(0, 8)}...
                    </td>

                    {/* User */}
                    <td className="px-5 py-4 font-semibold text-white">
                      <div>{o.user_name}</div>
                      <div className="text-[11px] text-gray-400 font-mono">TG: {o.telegram_id}</div>
                    </td>

                    {/* Stars Amount */}
                    <td className="px-5 py-4 font-mono font-bold text-nova-gold flex items-center space-x-1">
                      <Star className="w-3.5 h-3.5 fill-nova-gold text-nova-gold inline" />
                      <span>{o.stars_amount} XTR</span>
                    </td>

                    {/* Units */}
                    <td className="px-5 py-4 font-bold text-white">
                      {o.units_count}x Rig{o.units_count > 1 ? 's' : ''}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        o.status === 'paid'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : o.status === 'pending'
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {o.status}
                      </span>
                    </td>

                    {/* Telegram Charge ID */}
                    <td className="px-5 py-4 font-mono text-[11px]">
                      {o.telegram_payment_charge_id ? (
                        <span className="text-cyan-300 font-bold" title={o.telegram_payment_charge_id}>
                          {o.telegram_payment_charge_id.slice(0, 16)}...
                        </span>
                      ) : (
                        <span className="text-gray-500 italic">None</span>
                      )}
                    </td>

                    {/* Invoice Payload */}
                    <td className="px-5 py-4 font-mono text-[11px] text-gray-400" title={o.invoice_payload}>
                      {o.invoice_payload.slice(0, 14)}...
                    </td>

                    {/* Created At */}
                    <td className="px-5 py-4 text-gray-400 text-[11px]">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
